import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CreditCard, ArrowsLeftRight, ArrowUUpLeft, CircleNotch, CheckCircle } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const label = { color: "var(--bk-text-2)" };
const today = () => new Date().toISOString().slice(0, 10);

export function ProcessorPayouts({ businessId, onPosted }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [deposit, setDeposit] = useState("");
  const [date, setDate] = useState(today());
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  const load = useCallback(() => api.get(`/businesses/${businessId}/processor-payouts`).then((r) => setData(r.data)).catch(() => setData({ open_items: [], payouts: [], clearing_balance: 0 })), [businessId]);
  useEffect(() => { load(); }, [load]);

  if (!data || (!data.open_items.length && !data.payouts.length)) return null;

  const items = data.open_items;
  const gross = items.filter((i) => selected.includes(i.id)).reduce((s, i) => s + i.amount, 0);
  const dep = Number(deposit) || 0;
  const fee = selected.length && deposit !== "" ? Math.round((gross - dep) * 100) / 100 : null;
  const toggle = (id) => { setPlan(null); setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])); };
  const selectAll = () => { setPlan(null); setSelected(items.map((i) => i.id)); if (deposit === "") setDeposit(String(items.reduce((s, i) => s + i.amount, 0).toFixed(2))); };

  const review = async () => {
    setBusy(true);
    try {
      const { data: p } = await api.post(`/businesses/${businessId}/processor-payouts/preview`, { item_ids: selected, deposit_amount: dep, deposit_date: date });
      setPlan(p);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the match."); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/businesses/${businessId}/processor-payouts`, { item_ids: selected, deposit_amount: dep, deposit_date: date, confirm: true });
      toast.success(r.rowan_message);
      setPlan(null); setSelected([]); setDeposit("");
      await load(); onPosted && onPosted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post the match."); }
    finally { setBusy(false); }
  };
  const undo = async (p) => {
    if (!window.confirm(`Undo this ${money(p.deposit)} payout match? The deposit and fee entries are voided and ${p.item_count} item(s) go back to Merchant Processing Clearing.`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.delete(`/businesses/${businessId}/processor-payouts/${p.id}`);
      toast.success(r.rowan_message);
      await load(); onPosted && onPosted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't undo that."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bk-card p-5 space-y-4" data-testid="processor-payouts">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center justify-between gap-2 text-left" data-testid="processor-payouts-toggle">
        <span className="inline-flex items-center gap-2 font-semibold" style={{ color: "var(--bk-text)" }}>
          <CreditCard size={18} weight="duotone" color="var(--bk-accent)" /> Card processor payouts
        </span>
        <span className="text-xs" style={label} data-testid="processor-clearing-balance">
          {money(data.clearing_balance)} waiting in Merchant Processing Clearing · {items.length} unmatched item{items.length !== 1 ? "s" : ""}
        </span>
      </button>
      {open && (
        <>
          <p className="text-xs" style={label}>When the processor's deposit hits your bank, tick the card sales it covered and enter the amount deposited. Rowan moves that money from Merchant Processing Clearing into Bank and books the difference as processor fees — after you confirm.</p>
          {items.length > 0 && (
            <div className="space-y-1" data-testid="processor-open-items">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wider font-bold" style={label}>Unmatched card items</p>
                <button onClick={selectAll} className="text-xs font-semibold" style={{ color: "var(--bk-accent)" }} data-testid="processor-select-all">Select all</button>
              </div>
              {items.map((i) => (
                <label key={i.id} className="flex items-center gap-3 text-sm py-1.5 px-2 rounded-md cursor-pointer hover:bg-black/[0.03]" data-testid={`processor-item-${i.id}`}>
                  <input type="checkbox" checked={selected.includes(i.id)} onChange={() => toggle(i.id)} data-testid={`processor-item-check-${i.id}`} />
                  <span className="text-xs w-24 shrink-0" style={label}>{i.date}</span>
                  <span className="flex-1 truncate" style={{ color: "var(--bk-text)" }}>{i.description}</span>
                  <span className="font-mono text-sm" style={{ color: i.amount < 0 ? "#B23B1F" : "var(--bk-text)" }}>{i.amount < 0 ? "−" : ""}{money(Math.abs(i.amount))}</span>
                </label>
              ))}
              <div className="flex flex-wrap items-end gap-3 pt-3 border-t" style={{ borderColor: "var(--bk-border)" }}>
                <div>
                  <p className="text-[11px]" style={label}>Selected gross</p>
                  <p className="font-mono font-semibold" data-testid="processor-selected-gross">{money(gross)}</p>
                </div>
                <div>
                  <p className="text-[11px]" style={label}>Amount deposited to bank</p>
                  <input type="number" step="0.01" min="0" value={deposit} onChange={(e) => { setDeposit(e.target.value); setPlan(null); }} placeholder="0.00" data-testid="processor-deposit-amount"
                    className="rounded-md border px-3 py-2 text-sm outline-none w-36 font-mono" style={{ borderColor: "var(--bk-border)" }} />
                </div>
                <div>
                  <p className="text-[11px]" style={label}>Deposit date</p>
                  <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPlan(null); }} data-testid="processor-deposit-date"
                    className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                </div>
                <div>
                  <p className="text-[11px]" style={label}>Processor fee (gross − deposit)</p>
                  <p className="font-mono font-semibold" style={{ color: fee != null && fee < 0 ? "#B23B1F" : "var(--bk-text)" }} data-testid="processor-fee">{fee == null ? "—" : money(fee)}</p>
                </div>
                <button onClick={review} disabled={busy || !selected.length || dep <= 0 || (fee != null && fee < 0)} data-testid="processor-review-btn"
                  className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50">
                  <ArrowsLeftRight size={14} weight="bold" /> Review match
                </button>
              </div>
            </div>
          )}
          {plan && (
            <div className="rounded-2xl p-4 space-y-2" style={{ background: "var(--bk-rowan-bg)" }} data-testid="processor-confirm-panel">
              <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Confirm this match — nothing is posted until you do.</p>
              <ul className="text-sm font-mono space-y-0.5" data-testid="processor-plan-entries">
                {plan.entries.map((e, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>{e.debit ? "Dr" : "Cr"} {e.account} <span className="text-xs" style={label}>· {e.label}</span></span>
                    <span>{money(e.debit || e.credit)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs" style={label}>{plan.items.length} item{plan.items.length !== 1 ? "s" : ""} · {money(plan.gross)} gross = {money(plan.deposit)} deposit + {money(plan.fee)} fee · dated {plan.date}</p>
              <div className="flex gap-2 pt-1">
                <button onClick={confirm} disabled={busy} data-testid="processor-confirm-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50">
                  {busy ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />} Confirm & post
                </button>
                <button onClick={() => setPlan(null)} disabled={busy} data-testid="processor-cancel-btn" className="px-4 py-2 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Back</button>
              </div>
            </div>
          )}
          {data.payouts.length > 0 && (
            <div className="space-y-1" data-testid="processor-payout-history">
              <p className="text-[11px] uppercase tracking-wider font-bold" style={label}>Matched payouts</p>
              {data.payouts.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-3 text-sm py-1.5 px-2 rounded-md" data-testid={`processor-payout-${p.id}`}>
                  <span className="text-xs w-24 shrink-0" style={label}>{p.date}</span>
                  <span className="flex-1" style={{ color: "var(--bk-text)" }}>{money(p.deposit)} deposited · {p.item_count} item{p.item_count !== 1 ? "s" : ""} · {money(p.gross)} gross · {money(p.fee)} fees</span>
                  <button onClick={() => undo(p)} disabled={busy} data-testid={`processor-undo-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "#B23B1F" }}>
                    <ArrowUUpLeft size={13} weight="bold" /> Undo match
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
